import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

/**
 * Reusable confirmation dialog component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to close the dialog
 * @param {function} props.onConfirm - Function to call when confirmed
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Confirmation message
 * @param {string} [props.confirmText] - Text for confirm button (default: "Confirm")
 * @param {string} [props.cancelText] - Text for cancel button (default: "Cancel")
 * @param {string} [props.severity] - Dialog severity ('warning', 'error', 'info') (default: "warning")
 * @param {boolean} [props.loading] - Whether the confirm action is loading
 */
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
  loading = false,
}) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'error':
        return {
          icon: <DeleteIcon sx={{ fontSize: 48, color: '#f44336' }} />,
          confirmColor: 'error',
          iconBgColor: '#ffebee',
        };
      case 'warning':
        return {
          icon: <WarningIcon sx={{ fontSize: 48, color: '#ff9800' }} />,
          confirmColor: 'warning',
          iconBgColor: '#fff3e0',
        };
      case 'info':
      default:
        return {
          icon: <WarningIcon sx={{ fontSize: 48, color: '#2196f3' }} />,
          confirmColor: 'primary',
          iconBgColor: '#e3f2fd',
        };
    }
  };

  const config = getSeverityConfig(severity);

  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: config.iconBgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {config.icon}
          </Box>
          <Typography variant='h6' component='div' fontWeight='600'>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 3 }}>
        <DialogContentText
          sx={{
            fontSize: '1rem',
            color: 'text.primary',
            lineHeight: 1.6,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'center' }}>
        <Button
          onClick={handleClose}
          variant='outlined'
          color='inherit'
          startIcon={<CancelIcon />}
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color={config.confirmColor}
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
